// Экран разбора: главная линия партии, классификация ходов, eval bar, лента
// чипов, карточка хода, win%-граф и CTA. Seeded-путь (lichess [%eval]) —
// маркеры/бар/граф мгновенно. Без оценок — WASM-разбор движком с прогрессивным
// рендером (EngineController): листать можно сразу, аннотации дозаполняются.
// Единый путь кадра — renderFrame(ply): по ply красит доску/бар/карточку/чип/курсор/
// стрелку. Все входы (чипы, тулбар ⏮◀▶⏭, график, выход из variation/play) идут через неё.
import { MiniBoard } from '@ext/panel/board/mini-board';
import { evalGraphSvg } from '@ext/core/eval-graph';
import { winPct } from '@ext/core/evals';
import type { GameNode } from '@ext/core/types';
import { t } from '../i18n';
import { state, selectGame, selectedGame } from '../state';
import { showBackButton, hideMainButton, openLink, haptic, shareText, alertMsg } from '../tg';
import { buildReview, type ReviewModel } from '../core/review-model';
import { frameAt } from '../core/frame-model';
import { buildDeeplinkPayload } from '../core/deeplink';
import { chipLabel, errorState, fmtEval, headHtml, qClass, shellHtml } from './game-view';
import { EngineController } from './game-engine';
import { BoardToolbar, type BoardHost } from './board-toolbar';
import { ENGINE_DEPTH } from '../services/analysis';
import { commentFor, type CommentCtx, type Comment } from '../core/comments';
import { trackReviewOpened, trackCtaClick, trackTraining } from '../services/track';
import { VariationController } from './variation';
import { PlayEngineController } from './play-engine';
import { PLAY_OPTS, turnOf } from '../services/play-engine';
import { metaFromCoachGame, publishMyGame, saveMyGame } from '../services/my-games';

const EXT_URL = 'https://chromewebstore.google.com/detail/chess-analysis/enhokpgcomnpmbjancincfdppkldemhi';
// Main Mini App short name (getchesscoach бот). ?startapp=<payload> открывает
// конкретную партию; если payload собрать нельзя — деградируем в общую ссылку.
const SHARE_LINK = 'https://t.me/ChessAnalysis_by_ChessCoach_bot/analysis';

let activeController: EngineController | null = null;

/**
 * Центрировать активный чип В ЛЕНТЕ, меняя ТОЛЬКО горизонтальный scrollLeft
 * контейнера. scrollIntoView в TG-вебвью прокручивает всю страницу к доске даже с
 * block:'nearest' — здесь страница не трогается вовсе (фикс «пролистывается вверх»).
 */
function scrollChipIntoView(container: HTMLElement, chip: HTMLElement): void {
  const c = container.getBoundingClientRect();
  const b = chip.getBoundingClientRect();
  const delta = b.left - c.left - (container.clientWidth - chip.clientWidth) / 2;
  container.scrollLeft += delta;
}

/** Локализовать {piece}-параметр (тип фигуры → имя) для комментария/факта. */
function locPiece(params?: Record<string, string | number>): Record<string, string | number> | undefined {
  if (!params) return undefined;
  const p = { ...params };
  if (typeof p.piece === 'string') p.piece = t(`cmt.piece.${p.piece}`);
  return p;
}

/** Текст карточки: основной вердикт + тактический факт (второй строкой). */
function renderComment(c: Comment): string {
  const main = t(c.key, locPiece(c.params));
  return c.fact ? `${main}\n${t(c.fact.key, locPiece(c.fact.params))}` : main;
}

export function renderGame(root: HTMLElement, id: string): void {
  activeController?.destroy();
  activeController = null;

  const game = selectGame(id) || selectedGame();
  const isShared = Boolean(game && state.sharedId === game.id);
  const backHash = state.gameBackHash || (isShared ? '#/' : '#/games');
  if (!game) {
    location.hash = state.nick ? '#/games' : '#/';
    return;
  }
  let model: ReviewModel;
  try {
    model = buildReview(game, state.nick);
  } catch {
    errorState(root, backHash);
    return;
  }

  const willAnalyze = !model.engineReady;
  trackReviewOpened(game.site, willAnalyze ? 'wasm' : 'seeded');
  root.className = 'app v2';
  root.innerHTML = shellHtml(model, willAnalyze);
  hideMainButton();
  const goBack = (): void => {
    if (isShared) state.sharedId = null;
    state.gameBackHash = null;
    location.hash = backHash;
  };
  showBackButton(goBack);
  root.querySelector<HTMLElement>('[data-back]')?.addEventListener('click', goBack);
  root.querySelector<HTMLElement>('[data-ext]')?.addEventListener('click', () => {
    trackCtaClick('review_card');
    openLink(EXT_URL);
  });
  if (isShared && game.site !== 'pgn') {
    root.querySelector<HTMLElement>('[data-shared-cta]')?.classList.remove('hidden');
    root.querySelector<HTMLElement>('[data-analyze-own]')?.addEventListener('click', () => {
      trackCtaClick('analyze_own');
      state.sharedId = null;
      location.hash = '#/';
    });
  }

  // Импортированный или полученный по ссылке PGN: сохранить в избранное и
  // отправить другу. Здесь не показываем нерелевантный CTA «разобрать свои».
  const pgnActions = root.querySelector<HTMLElement>('[data-pgn-actions]');
  if (game.site === 'pgn') {
    pgnActions?.classList.remove('hidden');
    const saveBtn = root.querySelector<HTMLButtonElement>('[data-pgn-save]');
    saveBtn?.addEventListener('click', () => {
      void saveMyGame(metaFromCoachGame(game), game.pgn).then(() => {
        saveBtn.textContent = `★ ${t('my.saved')}`; saveBtn.disabled = true;
      }).catch((e) => alertMsg(t(e instanceof Error && e.message === 'limit' ? 'my.limit' : 'my.save_error')));
    });
    root.querySelector<HTMLButtonElement>('[data-pgn-share]')?.addEventListener('click', () => {
      const title = [game.white, game.black].filter(Boolean).join(' — ') || undefined;
      void publishMyGame(game.pgn, title).then((sharedId) => shareText(title || t('my.share_text'), `${SHARE_LINK}?startapp=my_${sharedId}`))
        .catch(() => alertMsg(t('my.share_error')));
    });
  }

  // Поделиться разбором: показываем, когда посчитана точность стороны пользователя.
  const shareRow = root.querySelector<HTMLElement>('[data-share-row]');
  const toastEl = root.querySelector<HTMLElement>('[data-toast]');
  let toastTimer = 0;
  function showToast(msg: string): void {
    if (!toastEl) return;
    toastEl.textContent = msg;
    toastEl.classList.remove('hidden');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toastEl.classList.add('hidden'), 2200);
  }
  function updateShareVisibility(): void {
    const ready = model.engineReady && model.accuracy != null && model.side != null;
    shareRow?.classList.toggle('hidden', !ready);
  }
  root.querySelector<HTMLElement>('[data-share]')?.addEventListener('click', () => {
    if (model.accuracy == null || model.side == null) return;
    haptic('medium');
    trackCtaClick('share');
    const opp = (model.side === 'w' ? model.black : model.white) || '—';
    const text = t('game.share_text', {
      acc: Math.round(model.accuracy),
      opp,
      blunders: model.counts.blunder,
      mistakes: model.counts.mistake,
    });
    const payload = buildDeeplinkPayload(game.site === 'lichess' ? 'lichess' : 'chesscom', state.nick, game.id);
    const link = payload ? `${SHARE_LINK}?startapp=${payload}` : SHARE_LINK;
    void shareText(text, link).then((res) => {
      if (res === 'copied') showToast(t('game.share_copied'));
    });
  });

  const boardEl = root.querySelector<HTMLElement>('[data-board]')!;
  let variation: VariationController | null = null;
  let play: PlayEngineController | null = null;
  const board = new MiniBoard(boardEl, {
    onMove: (san, fen) => {
      if (play?.active) play.handleUserMove(san, fen);
      else variation?.handleMove(san, fen);
    },
  });
  let orient: 'white' | 'black' = model.orientation;
  const syncBarFlip = (): void => {
    barEl?.classList.toggle('flipped', orient === 'black');
  };

  const stripEl = root.querySelector<HTMLElement>('[data-moves]')!;
  const chips = model.moves.map((mv) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.dataset.ply = String(mv.ply);
    b.addEventListener('click', () => renderFrame(mv.ply));
    stripEl.appendChild(b);
    return b;
  });

  const barEl = root.querySelector<HTMLElement>('[data-eval]');
  const cursorEl = root.querySelector<HTMLElement>('[data-cursor]');
  const headEl = root.querySelector<HTMLElement>('[data-head]')!;
  const cardTitle = root.querySelector<HTMLElement>('[data-card-title]')!;
  const cardBody = root.querySelector<HTMLElement>('[data-card-body]')!;
  const graphEl = root.querySelector<HTMLElement>('[data-graph]');
  const linesEl = root.querySelector<HTMLElement>('[data-lines]')!;
  const trainLinkEl = root.querySelector<HTMLAnchorElement>('[data-train-link]');
  const gameKey = `${game.site}:${game.id}`;
  trainLinkEl?.addEventListener('click', () => {
    if (trainLinkEl.dataset.ply) {
      location.hash = `#/train/blunders?g=${encodeURIComponent(gameKey)}&p=${trainLinkEl.dataset.ply}`;
    }
  });

  function redrawChips(): void {
    chips.forEach((c, i) => {
      const mv = model.moves[i]!;
      c.className = `move-chip ${qClass(mv.quality)} ${i === cur - 1 ? 'active' : ''}`.replace(/\s+/g, ' ').trim();
      c.textContent = chipLabel(mv);
    });
  }

  function redrawGraph(): void {
    if (!graphEl || model.graph.length <= 1) return;
    graphEl.innerHTML = `${evalGraphSvg(model.graph, model.moments, model.evals)}<div class="graph-cursor" data-cursor></div>`;
    const cur2 = graphEl.querySelector<HTMLElement>('[data-cursor]');
    if (cur2) cur2.style.left = `${(cur / Math.max(1, n)) * 100}%`;
  }

  if (graphEl) {
    graphEl.addEventListener('click', (e) => {
      if (!model.graph.length) return;
      const r = graphEl.getBoundingClientRect();
      const f = Math.max(0, Math.min(1, (e.clientX - r.left) / (r.width || 1)));
      renderFrame(Math.round(f * (model.graph.length - 1)));
    });
  }

  const n = model.moves.length;
  let cur = 0;

  function paintBar(pct: number): void {
    if (!barEl) return;
    barEl.style.setProperty('--white', String(Math.round(pct)));
    barEl.style.setProperty('--black', String(Math.round(100 - pct)));
  }

  function paintCursor(pct: number): void {
    const cur2 = graphEl?.querySelector<HTMLElement>('[data-cursor]') || cursorEl;
    if (cur2) cur2.style.left = `${pct}%`;
  }

  function setCard(ply: number): void {
    if (ply === 0) {
      cardTitle.textContent = t('game.start_pos');
      cardBody.textContent = model.engineReady ? t('game.start_note') : t('game.analyzing_note');
      trainLinkEl?.classList.add('hidden');
      return;
    }
    const mv = model.moves[ply - 1]!;
    const word = mv.quality ? t(`q.${mv.quality}`) : '';
    cardTitle.textContent = `${mv.moveNo}${mv.white ? '.' : '…'} ${mv.san}${word ? `  ·  ${word}` : ''}`;
    if (trainLinkEl) {
      const userWhite = model.side === 'w';
      const trainable =
        model.side != null &&
        mv.white === userWhite &&
        (mv.quality === 'blunder' || mv.quality === 'miss') &&
        Boolean(mv.node.parent?._bestmove);
      trainLinkEl.classList.toggle('hidden', !trainable);
      if (trainable) trainLinkEl.dataset.ply = String(mv.ply);
      else delete trainLinkEl.dataset.ply;
    }
    const ctx: CommentCtx = { evals: model.evals, depth: ENGINE_DEPTH, quality: mv.quality };
    const comment = commentFor(mv.node, ctx);
    if (comment) {
      cardBody.textContent = renderComment(comment);
    } else {
      cardBody.textContent =
        mv.evalAfterCp != null ? t('game.eval_after', { v: fmtEval(mv.evalAfterCp) }) : t('game.analyzing_note');
    }
  }

  function nodeAt(ply: number): GameNode {
    return ply === 0 ? model.root : model.moves[ply - 1]!.node;
  }

  /**
   * ЕДИНЫЙ путь кадра: по ply красит доску (позиция+ход+маркер), шкалу, карточку,
   * активный чип, курсор графика и стрелку (через controller.showFor). Сначала гасит
   * режим варианта — выход из variation/play возвращает доску сюда же.
   */
  function renderFrame(ply: number): void {
    variation?.reset();
    const f = frameAt(model, ply);
    cur = f.ply;
    board.setOrientation(orient);
    syncBarFlip();
    board.setPosition(f.fen, f.last);
    board.setMoveMarker(f.marker);
    paintBar(f.whitePct);
    setCard(cur);
    toolbar.refresh();
    redrawChips();
    const active = chips[cur - 1];
    if (active) scrollChipIntoView(stripEl, active);
    paintCursor(f.cursorPct);
    activeController?.showFor(nodeAt(cur));
  }

  function onRefresh(): void {
    headEl.innerHTML = headHtml(model);
    updateShareVisibility();
    redrawChips();
    redrawGraph();
    // В режиме варианта/игры доска/бар/карточка принадлежат им — только лента/граф/шапка.
    if (variation?.active || play?.active) return;
    const f = frameAt(model, cur);
    board.setMoveMarker(f.marker);
    paintBar(f.whitePct);
    setCard(cur);
  }

  const controller = new EngineController({
    model,
    board,
    gameId: `${game.site}:${game.id}`,
    linesEl,
    progressEl: root.querySelector<HTMLElement>('[data-progress]'),
    progressLabel: root.querySelector<HTMLElement>('[data-progress-label]'),
    progressBar: root.querySelector<HTMLElement>('[data-progress-bar]'),
    bestToggle: root.querySelector<HTMLInputElement>('[data-best-toggle]'),
    onRefresh,
  });
  activeController = controller;

  variation = new VariationController({
    board,
    engine: controller,
    root,
    cardTitle,
    cardBody,
    movesEl: root.querySelector<HTMLElement>('[data-var-moves]')!,
    returnBtn: root.querySelector<HTMLButtonElement>('[data-var-return]')!,
    getEntry: () => ({ ply: cur, node: nodeAt(cur) }),
    restore: (ply) => renderFrame(ply),
    // Оценка позиции варианта → eval bar (флип по ориентации делает syncBarFlip).
    onEval: (cp) => {
      if (cp != null) paintBar(winPct(cp));
    },
  });

  play = new PlayEngineController({
    board,
    host: root.querySelector<HTMLElement>('[data-play-host]')!,
    analyze: (fen) => controller.analyzePriority(fen, PLAY_OPTS),
    exitLabel: t('play.exit_game'),
    onExit: () => {
      renderFrame(cur);
      toolbar.onPlayExit();
    },
    onHintBusy: (busy) => toolbar.setHintBusy(busy),
  });

  const host: BoardHost = {
    first: () => renderFrame(0),
    prev: () => renderFrame(cur - 1),
    next: () => renderFrame(cur + 1),
    last: () => renderFrame(n),
    navState: () => {
      const f = frameAt(model, cur);
      return { atStart: f.atStart, atEnd: f.atEnd };
    },
    flip: () => {
      orient = orient === 'white' ? 'black' : 'white';
      board.setOrientation(orient);
    syncBarFlip();
    },
    applyBest: (show) => controller.setShowBest(show),
    startPlay: () => {
      variation?.reset();
      trackTraining('play_engine');
      const fen = nodeAt(cur).fen;
      play!.start(fen, turnOf(fen));
    },
    play: play!,
  };
  const toolbar = new BoardToolbar(host);
  root.querySelector<HTMLElement>('[data-nav]')!.replaceWith(toolbar.el);

  redrawChips();
  redrawGraph();
  updateShareVisibility();
  renderFrame(0);
  void controller.start(willAnalyze);
}
